import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { CropFree, MenuBook } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GradientSurface from '../../../components/shared/GradientSurface';
import GlassCard from '../../../components/shared/GlassCard';
import EmptyState from '../../../components/shared/EmptyState';
import { RADIUS } from '../../../theme/tokens';

/**
 * DigitalCardBack
 * Back face of the flip library card.
 * Caller is responsible for backfaceVisibility + rotateY(180deg) transform.
 * cardAds and trackAdClick must be passed in verbatim from the parent.
 *
 * @param {{ activeSubscription: object|null, qrUrl: string|null, qrLoaded: boolean,
 *           cardAds: Array, trackAdClick: (id: string) => void,
 *           onFullscreen: () => void }} props
 */
function DigitalCardBack({
  activeSubscription,
  qrUrl,
  qrLoaded,
  cardAds,
  trackAdClick,
  onFullscreen,
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Navy header */}
      <GradientSurface
        variant="navy"
        radius={0}
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: `${RADIUS.lg}px ${RADIUS.lg}px 0 0`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <MenuBook sx={{ fontSize: 20, color: 'white' }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white', fontStyle: 'italic' }}>
            BookMyLibrary.in
          </Typography>
        </Box>
        <CropFree sx={{ color: 'white', fontSize: 24 }} />
      </GradientSurface>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
          px: 2.5,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        {activeSubscription ? (
          <>
            <Typography variant="h6" fontWeight={700} color="text.primary" noWrap>
              {activeSubscription.library?.name ?? 'Library'}
            </Typography>

            <GlassCard tint="auto" sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                Package
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {activeSubscription.plan?.name ?? 'N/A'}
              </Typography>
            </GlassCard>

            <GlassCard tint="auto" sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                Valid Sessions
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {activeSubscription.remaining_sessions ?? 'N/A'}
              </Typography>
            </GlassCard>

            {/* Scan prompt + QR thumbnail */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/student/scanner');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: RADIUS.md,
                  '&:hover': { bgcolor: 'background.subtle' },
                }}
              >
                <CropFree sx={{ color: 'primary.main', fontSize: 28, flexShrink: 0, mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 110 }}>
                  Scan QR at library to Check-In / Check-Out
                </Typography>
              </Box>

              {qrUrl && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onFullscreen();
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: RADIUS.md,
                    p: 0.75,
                    border: `2px solid ${theme.palette.secondary.main}`,
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { boxShadow: theme.shadowsExt?.md },
                  }}
                >
                  {qrLoaded ? (
                    <img src={qrUrl} alt="QR Code" style={{ width: 72, height: 72 }} />
                  ) : (
                    <Box sx={{ width: 72, height: 72 }} />
                  )}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <EmptyState
            icon={<CropFree />}
            title="No active plan"
            description="Subscribe to a library plan to unlock your digital card details"
            actionLabel="Browse libraries"
            onAction={() => navigate('/student/libraries')}
            sx={{ py: 2 }}
          />
        )}
      </Box>

      {/* Offers footer */}
      {cardAds && cardAds.length > 0 && (
        <GradientSurface
          variant="navy"
          radius={0}
          sx={{
            px: 1.5,
            py: 1.25,
            flexShrink: 0,
            borderRadius: `0 0 ${RADIUS.lg}px ${RADIUS.lg}px`,
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ color: 'white', mb: 0.75, display: 'block' }}>
            Offers
          </Typography>
          <Box display="flex" gap={1}>
            {cardAds.slice(0, 2).map((ad, index) => (
              <Box
                key={ad.id || index}
                onClick={(e) => {
                  if (ad.link_url) {
                    e.stopPropagation();
                    trackAdClick(ad.id);
                    window.open(ad.link_url, '_blank');
                  }
                }}
                sx={{
                  bgcolor: ad.bg_color || theme.palette.primary.main,
                  borderRadius: RADIUS.sm,
                  px: 1.25,
                  py: 0.75,
                  flex: 1,
                  cursor: ad.link_url ? 'pointer' : 'default',
                  transition: 'transform 0.15s',
                  '&:hover': ad.link_url ? { transform: 'scale(1.02)' } : {},
                }}
              >
                <Typography
                  sx={{ color: ad.text_color || 'white', fontSize: '0.6875rem', display: 'block' }}
                  fontWeight={600}
                >
                  {ad.title}
                </Typography>
                <Typography
                  sx={{ color: ad.offer_color || theme.palette.accent?.main, fontSize: '0.8125rem' }}
                  fontWeight={700}
                >
                  {ad.offer_text}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            noWrap
            title="Card Terms: Valid for listed sessions only. Non-transferable."
            sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.6rem', mt: 0.75, display: 'block' }}
          >
            Card Terms: Valid for listed sessions only. Non-transferable.
          </Typography>
        </GradientSurface>
      )}
    </Box>
  );
}

export default DigitalCardBack;
